import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import React, { ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import Achievements from "../../components/profile/view/achievements";
import BasicDetails from "../../components/profile/view/basicDetails";
import ProfileStickyHeader from "../../components/profile/view/profileStickyHeader";
import FighterDetails from "../../components/profile/view/fighterDetails";
import FightHistory from "../../components/profile/view/fightHistory";
import ManageFighters from "../../components/profile/view/manageFighters";
import Sponsorships from "../../components/profile/view/sponsorships";
import YouTubeLinks from "../../components/profile/view/youtubeLinks";
import {
  createPublicProfileView,
  getAllRatings,
  getProfileById,
} from "../../services/api";
import { Profile, RatingResponseDto } from "../../types/types";
import Ratings from "../../components/profile/view/ratings";
import { PostSection } from "../../components/profile/view/postSection";

const cardClass =
  "bg-white rounded-3xl p-6 shadow-[0_0_1px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.06),0_2px_4px_rgba(0,0,0,0.04)]";

const CardShell = ({ children }: { children: ReactNode }) => (
  <div className={`${cardClass} h-full text-start`}>{children}</div>
);

const PublicViewProfile: React.FC = () => {
  const [searchParams] = useSearchParams();
  const profileId = searchParams.get("profileId");
  const [member, setMember] = useState<Profile | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [ratingData, setRatingData] = useState<RatingResponseDto[]>([]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [isRatingsLoading, setIsRatingsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRatingElements, setTotalRatingElements] = useState(0);
  const isRatingsLoadingRef = useRef(false);
  const [stickyNameVisible, setStickyNameVisible] = useState(false);

  const { t, i18n } = useTranslation("publicProfile");

  useEffect(() => {
    const savedLanguage = localStorage.getItem("i18nextLng") || "en";
    i18n.changeLanguage(savedLanguage);
  }, [i18n]);

  useEffect(() => {
    const fetchMember = async () => {
      try {
        if (!profileId) return;

        setLoading(true);
        const res = await getProfileById(profileId);
        if (res.success && res.data) {
          setMember(res.data);
        } else {
          setError(t("profile_not_found"));
        }
      } catch (err: any) {
        setError(err?.response?.data?.message || t("profile_not_found"));
      } finally {
        setLoading(false);
      }
    };

    const trackGeoLocation = async () => {
      try {
        const response = await fetch("https://ipapi.co/json");
        const data = await response.json();

        if (!profileId) return;

        const dto = {
          viewedProfileId: profileId,
          informationRequestDto: {
            ipAddress: data.ip,
            countryCode: data.country_code,
            city: data.city,
            browserInfo: navigator.userAgent,
            sourcePage: window.location.pathname,
          },
        };

        await createPublicProfileView(dto);
      } catch (error) {
        console.error("Geo tracking failed:", error);
      }
    };

    const handleTracking = () => {
      const key = `lastViewed_${profileId}`;
      const lastViewed = localStorage.getItem(key);

      if (!lastViewed || Date.now() - Number(lastViewed) > 300000) {
        trackGeoLocation();
        localStorage.setItem(key, Date.now().toString());
      } else {
        console.log("Skipping tracking: viewed recently");
      }
    };

    fetchMember();
    handleTracking();
  }, [profileId]);

  const loadRatingsAPI = async (page = 0) => {
    if (isRatingsLoadingRef.current || !profileId) return;
    isRatingsLoadingRef.current = true;
    setIsRatingsLoading(true);
    try {
      const res = await getAllRatings(profileId, page, 10);
      const content = res.content ?? [];
      setRatingData(content);

      setCurrentPage(res.number ?? page);
      setTotalPages(res.totalPages ?? 1);
      setTotalRatingElements(res.totalElements ?? 0);
    } catch (e) {
      console.error(e);
    } finally {
      isRatingsLoadingRef.current = false;
      setIsRatingsLoading(false);
    }
  };

  useEffect(() => {
    setRatingData([]);
    setCurrentPage(0);
    setTotalPages(1);
    setTotalRatingElements(0);
    if (profileId) {
      loadRatingsAPI(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const onScroll = () => {
      const hero = container.querySelector<HTMLElement>("[data-profile-hero]");
      const heroHeight = hero ? hero.offsetHeight : 350;
      setStickyNameVisible(container.scrollTop > heroHeight - 140);
    };

    onScroll();
    container.addEventListener("scroll", onScroll, { passive: true });
    return () => container.removeEventListener("scroll", onScroll);
  }, [loading, member]);

  if (loading) {
    return (
      <div
        className="mx-auto overflow-hidden h-auto min-h-screen lg:h-[90vh] relative flex flex-col bg-[#f4f4f5]"
        style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
      >
        <div className="flex-1 overflow-y-auto pb-10">
          <div className="relative w-full h-80 lg:h-[520px] overflow-hidden bg-slate-300 animate-pulse">
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-4 lg:px-8 lg:py-6 flex justify-between items-end gap-4">
              <div className="flex-1 flex flex-col justify-center items-start min-w-0 gap-3">
                <div className="h-8 lg:h-12 w-60 lg:w-96 rounded-xl bg-white/40 animate-pulse" />
                <div className="flex items-center gap-2">
                  <div className="h-5 w-24 rounded-lg bg-white/30 animate-pulse" />
                  <div className="h-5 w-32 rounded-lg bg-white/30 animate-pulse" />
                </div>
              </div>
              <div className="h-9 w-24 rounded-2xl bg-white/30 animate-pulse shrink-0" />
            </div>
          </div>

          <div className="px-4 lg:px-8 py-4 grid grid-cols-1 lg:grid-cols-[478px_minmax(0,1fr)] gap-6 items-stretch">
            <div className="bg-white rounded-3xl p-6 shadow-[0_0_1px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.06),0_2px_4px_rgba(0,0,0,0.04)] flex flex-col gap-4 animate-pulse">
              <div className="h-6 w-36 rounded-lg bg-slate-200" />
              <div className="space-y-3 pt-2">
                <div className="h-4 w-full rounded bg-slate-100" />
                <div className="h-4 w-5/6 rounded bg-slate-100" />
                <div className="h-4 w-3/4 rounded bg-slate-100" />
              </div>
              <div className="pt-3 flex flex-col gap-2.5">
                <div className="h-10 w-full rounded-2xl bg-slate-100" />
                <div className="h-10 w-full rounded-2xl bg-slate-100" />
              </div>
            </div>

            <div className="bg-white rounded-3xl p-6 shadow-[0_0_1px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.06),0_2px_4px_rgba(0,0,0,0.04)] flex flex-col gap-4 animate-pulse">
              <div className="h-6 w-44 rounded-lg bg-slate-200" />
              <div className="grid grid-cols-2 gap-4 pt-2">
                <div className="h-20 rounded-2xl bg-slate-100" />
                <div className="h-20 rounded-2xl bg-slate-100" />
                <div className="h-20 rounded-2xl bg-slate-100" />
                <div className="h-20 rounded-2xl bg-slate-100" />
              </div>
              <div className="space-y-2 pt-2">
                <div className="h-4 w-full rounded bg-slate-100" />
                <div className="h-4 w-4/5 rounded bg-slate-100" />
              </div>
            </div>
          </div>

          <div className="px-4 lg:px-8 py-2">
            <div className="bg-white rounded-3xl p-6 shadow-[0_0_1px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.06),0_2px_4px_rgba(0,0,0,0.04)] flex flex-col gap-4 animate-pulse">
              <div className="h-6 w-40 rounded-lg bg-slate-200" />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                <div className="h-24 rounded-2xl bg-slate-100" />
                <div className="h-24 rounded-2xl bg-slate-100" />
                <div className="h-24 rounded-2xl bg-slate-100" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !member) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg text-red-600">
          {error || t("profile_not_found")}
        </div>
      </div>
    );
  }

  const ratingsContent = (
    <>
      <Ratings
        member={member}
        ratings={ratingData}
        currentPage={currentPage}
        totalPages={totalPages}
        totalElements={totalRatingElements}
        isLoading={isRatingsLoading}
        onPageChange={loadRatingsAPI}
      />
      {isRatingsLoading && (
        <div className="w-full py-4 flex justify-center items-center">
          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-gray-500"></div>
        </div>
      )}
    </>
  );

  const tabs: { id: string; title: string; content: ReactNode }[] = [
    {
      id: "basic-details",
      title: t("basic_details").toUpperCase(),
      content: (
        <BasicDetails
          member={member}
          showCopyButton={true}
          isOwnProfile={false}
          reviewCount={totalRatingElements}
        />
      ),
    },
  ];

  if (member.profileType === "MANAGER" || member.manager) {
    tabs.push({
      id: "manage-fighters",
      title: t("manage_fighters").toUpperCase(),
      content: (
        <CardShell>
          <ManageFighters member={member} isOwnProfile={false} />
        </CardShell>
      ),
    });

    tabs.push({
      id: "ratings",
      title: t("ratings").toUpperCase(),
      content: <CardShell>{ratingsContent}</CardShell>,
    });
  } else if (member.fighter) {
    tabs.push(
      {
        id: "fighter-details",
        title: t("fighter_details").toUpperCase(),
        content: <FighterDetails member={member} />,
      },
      {
        id: "fight-history",
        title: t("fight_history").toUpperCase(),
        content: (
          <CardShell>
            <FightHistory member={member} />
          </CardShell>
        ),
      },
      {
        id: "achievements",
        title: t("achievements").toUpperCase(),
        content: (
          <CardShell>
            <Achievements member={member} />
          </CardShell>
        ),
      },
      {
        id: "youtube-links",
        title: t("youtube_links").toUpperCase(),
        content: (
          <CardShell>
            <YouTubeLinks member={member} />
          </CardShell>
        ),
      },
      {
        id: "sponserships",
        title: t("sponserships").toUpperCase(),
        content: <Sponsorships member={member} />,
      },
      {
        id: "ratings",
        title: t("ratings").toUpperCase(),
        content: <CardShell>{ratingsContent}</CardShell>,
      }
    );
  }

  tabs.push({
    id: "posts",
    title: t("feed", { defaultValue: "FEED" }).toUpperCase(),
    content: (
      <div className="w-full max-w-[680px] mx-auto py-4">
        <h3 className="text-center text-sm font-medium text-gray-900 mb-6">
          {t("feed", { defaultValue: "Feed" })}
        </h3>
        <PostSection profileId={profileId ?? member.id} />
      </div>
    ),
  });

  const layoutRows: string[][] = [
    ["basic-details"],
    ["fighter-details", "fight-history"],
    ["youtube-links"],
    ["achievements"],
    ["sponserships"],
    ["manage-fighters"],
    ["ratings"],
    ["posts"],
  ];
  const tabById = Object.fromEntries(tabs.map((tb) => [tb.id, tb]));

  return (
    <div
      className="mx-auto overflow-hidden h-auto min-h-screen lg:h-[90vh] relative flex flex-col bg-[#f4f4f5]"
      style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
    >
      <div className="shrink-0 bg-white h-16 lg:h-[84px] px-4 lg:px-6 flex items-center justify-between gap-3 border-b border-gray-100">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label={t("back", { defaultValue: "Back" })}
            className="h-10 w-10 shrink-0 rounded-full bg-gray-100 text-blue-600 inline-flex items-center justify-center hover:bg-gray-200 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <h1 className="text-xl lg:text-2xl font-semibold text-gray-900 truncate">
            {t("profile", { defaultValue: "Profile" })}
          </h1>
        </div>
      </div>

      <div
        ref={scrollContainerRef}
        className="flex-1 min-h-0 overflow-y-auto pb-10"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        <ProfileStickyHeader
          member={member}
          visible={stickyNameVisible}
          reviewCount={totalRatingElements}
        />
        <style
          dangerouslySetInnerHTML={{
            __html: `
              .flex-1.overflow-y-auto::-webkit-scrollbar {
                display: none;
              }
            `,
          }}
        />
        {layoutRows.map((ids) => {
          const items = ids.map((id) => tabById[id]).filter(Boolean);
          if (!items.length) return null;
          const isHero = ids[0] === "basic-details";
          const sideBySide = items.length > 1;

          return (
            <React.Fragment key={ids.join("-")}>
              <div
                className={
                  isHero
                    ? ""
                    : `px-4 lg:px-8 py-3 ${
                        sideBySide
                          ? "grid grid-cols-1 lg:grid-cols-[478px_minmax(0,1fr)] gap-6 items-stretch"
                          : ""
                      }`
                }
              >
                {items.map((tab) => (
                  <div
                    key={tab.id}
                    ref={(el) => {
                      sectionRefs.current[tab.id] = el;
                    }}
                    id={tab.id}
                    className={sideBySide ? "h-full" : ""}
                  >
                    {tab.content}
                  </div>
                ))}
              </div>

            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default PublicViewProfile;
